import { useTranslation } from "next-i18next";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useRouter } from "next/router";

import { MODAL_NAME } from "@/components/editor/stripe/constants";
import { useAppDispatch } from "@/hooks/redux";
import { projectActions } from "@/redux/stores/projectStore";
import { useSubscription } from "@/hooks/useSubscription";
import { paymentApi } from "@/redux/api/paymentApi";
import { useNotification } from "@/hooks/useNotification";

import { IconPurchaseDiscount, IconPurchaseTeamShare } from "../Icons";

import { PurchasePopup } from "./PurchasePopup";

export const SubscriptionPurchasePopup = ({
  isOpened = false,
  onClose,
  plan,
  period,
  seats,
}: {
  isOpened?: boolean;
  onClose?: () => void;
  plan?: "personal" | "team";
  period?: "monthly" | "yearly";
  seats?: number;
}) => {
  const { t } = useTranslation("common");
  const router = useRouter();

  const { contextHolder, notify } = useNotification();

  const dispatch = useAppDispatch();

  /**
   * 마운트 여부 스테이트
   *
   * 팝업이 열리고 잠시 후에 마운트 여부를 true로 취급
   */
  const [isMounted, setIsMounted] = useState(false);

  useEffect(() => {
    if (isOpened) {
      setIsMounted(true);
    } else {
      setIsMounted(false);
    }

    return () => {
      setIsMounted(false);
    };
  }, [isOpened]);

  const { data: plans } = paymentApi.useGetPlansQuery({});

  const {
    plan: currentPlan,
    subscriptionInfo,
    refetch: refetchSubscription,
  } = useSubscription();

  /**
   * 현재 구독 상태 등 정리
   */
  const currentPlanFullName = useMemo(() => {
    return `${currentPlan?.raw} (${
      currentPlan?.interval === "month"
        ? "Monthly"
        : currentPlan?.interval === "year"
        ? "Annual"
        : ""
    })`;
  }, [currentPlan]);
  const currentBillingCycle = useMemo(() => {
    if (!currentPlan?.servicePeriodStart || !currentPlan?.servicePeriodEnd) {
      return;
    }

    return {
      start: currentPlan.servicePeriodStart,
      end: currentPlan.servicePeriodEnd,
    };
  }, [currentPlan]);

  const currentPlanPriceId = useMemo(() => {
    const planType =
      currentPlan?.interval === "month"
        ? "monthly"
        : currentPlan?.interval === "year"
        ? "yearly"
        : undefined;

    if (!planType) {
      return;
    }

    const foundCurrentPlan = plans?.find(
      (p) =>
        p.mark === "unlimit" &&
        p.planName.toLowerCase().includes(currentPlan?.normalized ?? ""),
    );

    if (!foundCurrentPlan) {
      return;
    }

    return foundCurrentPlan.planId[planType];
  }, [currentPlan, plans]);

  /**
   * 현재 구매한 애드온 크레딧
   */
  const subscriptionId = subscriptionInfo?.subscriptionId;
  const currentAddOn = useMemo(() => {
    const matchingPlanName = currentPlan?.normalized;

    if (!matchingPlanName || !subscriptionInfo) {
      return;
    }

    return subscriptionInfo.items?.find((item) => {
      const lowerCasedPlanName = item.planName.toLocaleLowerCase();

      return (
        lowerCasedPlanName.includes("generative") &&
        lowerCasedPlanName.includes(matchingPlanName)
      );
    });
  }, [subscriptionInfo, currentPlan]);
  const subscriptionItemId = currentAddOn?.subscriptionItemId;

  /**
   * 취소, 다운그레이드 등 예약된 건이 있는지 확인
   */
  const isScheduledDowngrade = useMemo(() => {
    const schedule = subscriptionInfo?.schedule;
    if (!schedule) {
      return false;
    }

    const currentScheduleItems = schedule.current;
    const nextScheduleItems = schedule.next;

    if (!currentScheduleItems || !Array.isArray(currentScheduleItems)) {
      return false;
    }

    if (!nextScheduleItems || !Array.isArray(nextScheduleItems)) {
      return false;
    }

    for (const currentScheduleItem of currentScheduleItems) {
      const nextScheduleItem = nextScheduleItems.find((item) => {
        return item.price === currentScheduleItem.price;
      });

      const currentScheduleItemQuantity = currentScheduleItem.quantity;
      const nextScheduleItemQuantity = nextScheduleItem?.quantity ?? 0;

      if (currentScheduleItemQuantity > nextScheduleItemQuantity) {
        return true;
      }
    }

    return false;
  }, [subscriptionInfo?.schedule]);

  /**
   * 선택한 플랜
   */
  const foundPlan = useMemo(() => {
    if (!plans || !plan) {
      return;
    }

    return plans.find((p) => {
      const lowerCasedPlanName = p.planName.toLowerCase();
      return p.mark === "unlimit" && lowerCasedPlanName.includes(plan);
    });
  }, [plans, plan]);
  const foundPlanName = foundPlan?.planName;
  const foundPlanPricePerMonth =
    period === "monthly"
      ? foundPlan?.pricePerMonth
      : period === "yearly"
      ? foundPlan?.pricePerMonthByYearly
      : undefined;
  const foundPlanPriceId =
    period === "monthly"
      ? foundPlan?.planId.monthly
      : period === "yearly"
      ? foundPlan?.planId.yearly
      : undefined;

  const initialSeats = seats ?? 1;

  const [editingSeats, setEditingSeats] = useState(initialSeats);

  useEffect(() => {
    setEditingSeats(initialSeats);
  }, [isOpened, initialSeats]);

  const editingProducts = useMemo(() => {
    return [
      {
        productTitle: foundPlanName,
        productPrice: `$${foundPlanPricePerMonth}`,
        isMonthly: true,
        isBilledAnnually: period === "yearly",
        unit: t("Seats"),
        min: 1,
        descriptions: [
          plan === "team" ? (
            <>
              <IconPurchaseTeamShare />
              {t("Team plans include shared access and expanded limits.")}
            </>
          ) : (
            <>
              <IconPurchaseDiscount />
              {t(
                "Save more with annual billing and enjoy uninterrupted access.",
              )}
            </>
          ),
        ],
        isVisibleQuantity: plan === "team",
        isDisabledQuantity: plan === currentPlan?.normalized,
        quantity: editingSeats,
        isSelected: true,
      },
    ];
  }, [
    foundPlanName,
    foundPlanPricePerMonth,
    period,
    plan,
    currentPlan,
    editingSeats,
    t,
  ]);

  /**
   * 오늘 결제 금액 계산 관련
   */
  // debounce 해서 숫자가 바뀌면 바로 조회하지 않고 잠시 후에 조회하도록 함
  const [debouncedEditingSeats, setDebouncedEditingSeats] = useState(
    () => editingSeats || 1,
  );

  useEffect(() => {
    setDebouncedEditingSeats(initialSeats || 1);
  }, [isOpened, initialSeats]);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (editingSeats % 1 !== 0) {
        return;
      }

      setDebouncedEditingSeats(editingSeats);
    }, 1000);

    return () => {
      clearTimeout(timeoutId);
    };
  }, [editingSeats]);

  const isQuantityChanged = debouncedEditingSeats !== editingSeats;

  const invoicePreviewType = useMemo(() => {
    const changingPriceId = foundPlanPriceId;

    if (!currentPlanPriceId || !changingPriceId) {
      return;
    }

    if (currentPlanPriceId !== changingPriceId) {
      return "base";
    } else {
      return "addon";
    }
  }, [currentPlanPriceId, foundPlanPriceId]);

  const {
    data: invoicePreviewData,
    isFetching: isInvoicePreviewFetching,
    isError: isInvoicePreviewError,
  } = paymentApi.useGetInvoicePreviewQuery(
    {
      type: invoicePreviewType!,
      subscriptionId: subscriptionId ?? "",
      items: [
        {
          id: invoicePreviewType === "addon" ? subscriptionItemId : undefined,
          price: foundPlanPriceId ?? "",
          quantity: debouncedEditingSeats,
        },
      ],
    },
    {
      skip:
        !isMounted ||
        !invoicePreviewType ||
        !subscriptionId ||
        !foundPlanPriceId ||
        !debouncedEditingSeats,
    },
  );

  const totalPrice = useMemo(() => {
    const items = invoicePreviewData?.items;

    if (!items || !Array.isArray(items)) {
      return 0;
    }

    return items
      .filter((item) => !item.proration)
      .reduce((acc, item) => {
        return acc + (item.unit_amount ?? 0) * (item.quantity ?? 0);
      }, 0);
  }, [invoicePreviewData]);

  const todayBillingTotalPrice = invoicePreviewData?.due_today ?? 0;

  let discountInfo = null;
  const discount = subscriptionInfo?.discount;
  if (discount) {
    if (discount.interval === "yearly") {
      discountInfo = {
        title: "10% off",
      };
    } else {
      discountInfo = {
        title: "50% off",
      };
    }
  }

  const { details, prorationDetails } = useMemo(() => {
    if (!period || !invoicePreviewData) {
      return {};
    }

    const items = invoicePreviewData.items ?? [];

    const mergingDetails = [];
    const mergingProrationDetails = [];

    const getDescription = (item: (typeof items)[number]) => {
      const pName = item.planName;
      const pInterval = item.price.recurring.interval;
      const pQuantity = item.quantity;

      if (!item.price.lookup_key) return "";
      if (item.price.lookup_key.includes("base")) {
        return `(${pInterval === "month" ? "Monthly" : "Annual"})${
          pName.toLowerCase().includes("team")
            ? ` x ${pQuantity} ${t("Seat")}`
            : ""
        }`;
      } else if (item.price.lookup_key.includes("avatar")) {
        return `(${
          pInterval === "month" ? "Monthly" : "Annual"
        }) x ${pQuantity} ${t("Qty")}`;
      } else if (item.price.lookup_key.includes("genCredit")) {
        const credit = Number(
          item.description?.split(" - ")?.[1]?.substring(0, 3) ?? "0",
        );
        const month = pInterval === "month" ? 1 : 12;
        return `(${credit * month} credits) x ${pQuantity} ${t("Unit")}`;
      }
    };

    const subscriptionInterval = subscriptionInfo?.interval;
    const transformedSubscriptionInterval =
      subscriptionInterval === "month" ? "monthly" : "yearly";
    const isSameInterval = period === transformedSubscriptionInterval;

    const getPriority = (item: (typeof items)[number]) => {
      if (!item.price.lookup_key) {
        return 999999;
      }

      if (item.price.lookup_key.includes("base")) {
        return 0;
      }

      if (item.price.lookup_key.includes("avatar")) {
        return 11;
      }

      if (item.price.lookup_key.includes("genCredit")) {
        return 12;
      }

      return 999999;
    };

    const sortedItems = [...items].sort((a, b) => {
      const aPriority = getPriority(a);
      const bPriority = getPriority(b);

      return aPriority - bPriority;
    });

    for (const item of sortedItems) {
      const entry = {
        title: item.planName,
        description: getDescription(item),
        price: item.amount,
      };

      const isAvatar = item.price.lookup_key.includes("avatar");
      const notProrationItem = isSameInterval && !item.proration;

      if (item.amount > 0) {
        if (notProrationItem && isAvatar) {
          mergingDetails.push(entry);
          mergingProrationDetails.push({
            ...entry,
            price: entry.price * -1,
          });
        } else if (!notProrationItem) {
          mergingDetails.push(entry);
        }
      } else {
        mergingProrationDetails.push(entry);
      }
    }

    return {
      details: mergingDetails,
      prorationDetails: mergingProrationDetails,
    };
  }, [invoicePreviewData, period, subscriptionInfo, t]);

  const nextBillingTotalPrice = totalPrice;

  const { nextBillingCycle, nextBillingDate } = useMemo(() => {
    if (isInvoicePreviewFetching) {
      return {};
    }

    const subscriptionInterval = subscriptionInfo?.interval;
    const transformedPeriod = period === "monthly" ? "month" : "year";

    const nextPeriodInfo = invoicePreviewData?.items?.find((item) => {
      return item.price.recurring.interval === transformedPeriod;
    });

    const nextPeriodStart = nextPeriodInfo?.period?.start;
    const nextPeriodEnd = nextPeriodInfo?.period?.end;

    const nextPeriodBillingDate = nextPeriodEnd
      ? new Date(nextPeriodEnd * 1000)
      : currentBillingCycle?.end;

    if (
      transformedPeriod !== subscriptionInterval &&
      nextPeriodStart &&
      nextPeriodEnd
    ) {
      return {
        nextBillingCycle: {
          start: new Date(nextPeriodStart * 1000),
          end: new Date(nextPeriodEnd * 1000),
        },
        nextBillingDate: nextPeriodBillingDate,
      };
    }

    return {
      nextBillingDate: nextPeriodBillingDate,
    };
  }, [
    invoicePreviewData,
    period,
    subscriptionInfo,
    currentBillingCycle,
    isInvoicePreviewFetching,
  ]);

  /**
   * 결제 등 진행
   */
  const [changeAddOn, { isLoading: isLoadingChangeAddOn }] =
    paymentApi.useChangeAddOnMutation();
  const [upgradeSubscription, { isLoading: isLoadingUpgradeSubscription }] =
    paymentApi.useUpgradeSubscriptionMutation();

  const handleConfirm = useCallback(async () => {
    if (isLoadingChangeAddOn || isLoadingUpgradeSubscription) {
      return;
    }

    if (!subscriptionId || !foundPlanPriceId || !debouncedEditingSeats) {
      return;
    }

    if (isQuantityChanged) {
      return;
    }

    /**
     * 비로그인, 엔터프라이즈, 무료 플랜은 구매 불가
     *
     * 애초에 이 팝업이 표시되지 않음
     */
    if (currentPlan?.level.current === 0 || currentPlan?.level.current === 3) {
      return;
    }

    /**
     * 무제한 플랜이 아닌 경우 플랜 변경 팝업 표시
     */
    if (!currentPlan?.unlimited) {
      dispatch(
        projectActions.setCurrentPlanAndOpenedModal({
          currentPlan: currentPlan,
          openedModal: MODAL_NAME.PLAN_SWITCH,
        }),
      );

      onClose?.();

      return;
    }

    try {
      /**
       * 현재 플랜의 아이디와 변경할 플랜의 아이디가 같은 경우\
       * 시트 수만 변경하는 건임
       *
       * 시트 수만 변경할 때는 애드온 변경 API를 사용함\
       * 플랜의 변경이 발생할 때는 업그레이드 API를 사용함
       *
       * 하지만 현재는 시트 수만 변경하는 케이스는 없음
       */
      if (currentPlanPriceId === foundPlanPriceId) {
        await changeAddOn({
          subscriptionId,
          items: [
            {
              id: subscriptionItemId,
              price: foundPlanPriceId ?? "",
              quantity: debouncedEditingSeats,
            },
          ],
        }).unwrap();
      } else {
        await upgradeSubscription({
          subscriptionId,
          items: [
            {
              price: foundPlanPriceId ?? "",
              quantity: debouncedEditingSeats,
            },
          ],
        }).unwrap();
      }

      await refetchSubscription();

      onClose?.();

      notify.success(
        t("Your Add-On has been updated and the changes are now in effect."),
      );

      router.reload();
    } catch (e) {
      // TODO: 에러 종류에 따라 다른 메시지 추가 필요
      if (false) {
        // 사용자 결제 정보 오류로 인한 결제 실패인 경우
        notify.warning(
          t(
            "Add-On update failed. Please check your billing info and try again.",
          ),
        );
      } else {
        // 그 외 일반적인 오류
        notify.warning(
          t(
            "Add-On update failed. Please try again. If the issue persists, contact support.",
          ),
        );
      }
    }
  }, [
    subscriptionId,
    foundPlanPriceId,
    debouncedEditingSeats,
    isQuantityChanged,
    currentPlan,
    currentPlanPriceId,
    dispatch,
    changeAddOn,
    upgradeSubscription,
    onClose,
    subscriptionItemId,
    refetchSubscription,
    notify,
    t,
    isLoadingChangeAddOn,
    isLoadingUpgradeSubscription,
    router,
  ]);

  /**
   * 결제 포탈 이동
   */

  const [successUrl, setSuccessUrl] = useState("");

  useEffect(() => {
    setSuccessUrl(window.location.href);
  }, [router]);

  const { data: billingPortalUrl } = paymentApi.useGetBillingPortalUrlQuery(
    {
      successUrl,
    },
    {
      skip: !isMounted || !successUrl,
    },
  );

  const goToBillingPortal = useCallback(async () => {
    if (!billingPortalUrl) {
      return;
    }

    window.open(billingPortalUrl, "_blank");
  }, [billingPortalUrl]);

  return (
    <>
      <PurchasePopup
        isOpened={isOpened}
        onClose={() => {
          if (isLoadingChangeAddOn || isLoadingUpgradeSubscription) {
            return;
          }

          onClose?.();
        }}
        title={t("Update Subscription")}
        productsTitle={t("AI Studios Plan")}
        productsSubTitle={t(
          "Upgrade your plan to unlock more features and flexibility",
        )}
        products={editingProducts}
        onChangeProductQuantity={(index, quantity) => {
          setEditingSeats(quantity);
        }}
        productsDescriptions={[
          t("Your subscription will be updated immediately."),
          t("Charges will be prorated based on your billing cycle."),
          t(
            "All plan benefits and add-ons will be updated to fit your new subscription.",
          ),
        ]}
        currentPlan={currentPlanFullName}
        currentBillingCycle={currentBillingCycle}
        nextBillingCycle={nextBillingCycle}
        totalPrice={totalPrice}
        details={details}
        prorationDetails={prorationDetails}
        todayPrice={todayBillingTotalPrice}
        loadingTodayPrice={isInvoicePreviewFetching || isQuantityChanged}
        nextBillingPrice={nextBillingTotalPrice}
        nextBillingDate={nextBillingDate}
        confirmText={t("Confirm")}
        billingCycleCautionTitle={
          isScheduledDowngrade ? t("📌 Upgrade notice") : undefined
        }
        billingCycleCautionDescription={
          isScheduledDowngrade
            ? t(
                "Any scheduled cancellation or downgrade will be automatically removed when you upgrade your plan. Your new plan will take effect immediately, and you'll be charged today.",
              )
            : undefined
        }
        disabledConfirm={isInvoicePreviewError || isQuantityChanged}
        loadingConfirm={isLoadingChangeAddOn || isLoadingUpgradeSubscription}
        onConfirm={handleConfirm}
        goToBillingPortal={goToBillingPortal}
        discount={discountInfo}
      />

      {contextHolder}
    </>
  );
};

/**
 * plans에서 detail을 가공하는 이전 로직 기록해둠\
 * 현재는 다른 방식으로 변경했음\
 * 이전 로직을 되살리고 싶으면 아래 참고
 */
/*
const details = useMemo(() => {
  const items = invoicePreviewData?.items;

  if (!items || !Array.isArray(items)) {
    return [];
  }

  const subscriptionPlanItem = items.find((item) => {
    return item.planName === foundPlanName;
  });

  const customAvatarPlan = plans?.find((p) => {
    const lowerCasedPlanName = p.planName.toLowerCase();

    return lowerCasedPlanName.includes("avatar");
  });

  const customAvatarItem = items.find((item) => {
    return item.planName === customAvatarPlan?.planName;
  });

  const generativeCreditsPlans = plans?.filter((p) => {
    const lowerCasedPlanName = p.planName.toLowerCase();

    return lowerCasedPlanName.includes("generative");
  });

  const generativeCreditsItem = items.find((item) => {
    return generativeCreditsPlans?.find((p) => p.planName === item.planName);
  });

  const generativeCreditsPlan = generativeCreditsPlans?.find((p) => {
    return p.planName === generativeCreditsItem?.planName;
  });

  const generativeCreditsConstraints =
    period === "monthly"
      ? generativeCreditsPlan?.constraints.monthly
      : period === "yearly"
      ? generativeCreditsPlan?.constraints.yearly
      : undefined;
  const generativeCreditsPerUnit = generativeCreditsConstraints?.genCredit;

  const mergingDetails = [];

  if (subscriptionPlanItem) {
    mergingDetails.push({
      title: subscriptionPlanItem.planName,
      description: `(${period === "monthly" ? "Monthly" : "Annual"})${
        plan === "team"
          ? ` x ${subscriptionPlanItem.quantity} ${t("Seats")}`
          : ""
      }`,
      price:
        (subscriptionPlanItem.unit_amount ?? 0) *
        (subscriptionPlanItem.quantity ?? 0),
    });
  }

  if (customAvatarItem) {
    mergingDetails.push({
      title: customAvatarItem.planName,
      description: `x ${customAvatarItem.quantity} ${t("Qty")}`,
      price:
        (customAvatarItem.unit_amount ?? 0) *
        (customAvatarItem.quantity ?? 0),
    });
  }

  if (generativeCreditsItem) {
    mergingDetails.push({
      title: generativeCreditsItem.planName,
      description: `(${generativeCreditsPerUnit} credits) x ${
        generativeCreditsItem.quantity
      } ${t("Units")}`,
      price:
        (generativeCreditsItem.unit_amount ?? 0) *
        (generativeCreditsItem.quantity ?? 0),
    });
  }

  return mergingDetails;
}, [plan, invoicePreviewData, foundPlanName, period, t, plans]);
*/
